import { useEffect, useState } from 'react';

/**
 * Tracks browser connectivity via `navigator.onLine` + the `online`/`offline`
 * window events. This is the ONLY connectivity signal in the app today — no
 * RPC-failure-based detector existed before this hook (grepped for
 * `navigator.onLine`/offline listeners across web/src and web/api, zero hits).
 * Deliberately minimal: `navigator.onLine` only promises "definitely offline"
 * is reliable (a `true` reading can still mean no route to the actual API),
 * but it's the same signal every other offline-aware web app starts from, and
 * good enough for "don't pretend stamping works when the OS says there's no
 * link at all" (mockup #10b).
 */
export function useOnline(): boolean {
  const [online, setOnline] = useState<boolean>(() =>
    typeof navigator === 'undefined' ? true : navigator.onLine,
  );

  useEffect(() => {
    const goOnline = () => setOnline(true);
    const goOffline = () => setOnline(false);
    window.addEventListener('online', goOnline);
    window.addEventListener('offline', goOffline);
    return () => {
      window.removeEventListener('online', goOnline);
      window.removeEventListener('offline', goOffline);
    };
  }, []);

  return online;
}
